import React, { useState, useEffect } from 'react';
import { Building2, DollarSign, TrendingUp, AlertCircle, BellRing, Plus, Send, X, BarChart3, AlertTriangle } from 'lucide-react';
import { db } from '../../services/firebase';
import { collection, query, getDocs, orderBy, limit, deleteDoc, collectionGroup, doc, onSnapshot, serverTimestamp, updateDoc, where, writeBatch } from 'firebase/firestore';
import LoadingState from '../shared/LoadingState';
import { sendCompanyNoticeEmail } from '../../services/emailService';
import Companies from './Companies';
import './RootDashboard.css';
import { useSearchParams } from 'react-router-dom';
import { calculateSystemNetReceipt } from '../../utils/billingPlans';

const RootDashboard = () => {
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    totalRevenue: 0,
    monthRevenue: 0,
    systemNetReceipt: 0,
    loading: true
  });
  const [companies, setCompanies] = useState([]);
  const [accessLogs, setAccessLogs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [billingReceipts, setBillingReceipts] = useState([]);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [systemErrors, setSystemErrors] = useState([]);
  const [selectedSystemError, setSelectedSystemError] = useState(null);
  const [activeTab, setActiveTab] = useState('panel');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const requestedTab = searchParams.get('rootTab');
    if (['panel', 'billing', 'companies', 'logs'].includes(requestedTab)) {
      setActiveTab(requestedTab);
    } else {
      setActiveTab('panel');
    }
  }, [searchParams]);
  const seenReceiptIds = React.useRef(new Set());
  const receiptsInitialized = React.useRef(false);
  const seenErrorIds = React.useRef(new Set());
  const errorsInitialized = React.useRef(false);
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [sendingNotice, setSendingNotice] = useState(false);
  const [noticeForm, setNoticeForm] = useState({ companyId: '', type: 'billing_due', title: 'Cobrança do plano', message: '', dueDate: '', amount: '', pixKey: '' });

  useEffect(() => {
    // Setup real-time listeners for companies and reservations
    const companiesRef = collection(db, 'companies');
    const reservationsGroup = collectionGroup(db, 'reservations');

    // Local maps to correlate companies and reservations
    let companiesMap = new Map();

    const companiesUnsub = onSnapshot(companiesRef, companiesSnap => {
      companiesMap = new Map();
      let activeCount = 0;
      companiesSnap.forEach(doc => {
        const data = { id: doc.id, ...doc.data(), revenue: 0, monthRevenue: 0 };
        companiesMap.set(doc.id, data);
        if (data.active) activeCount++;
      });

      // update companies list (reservations will merge in via reservations listener)
      const list = Array.from(companiesMap.values()).sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
      setCompanies(list);
      setStats(prev => ({ ...prev, totalCompanies: companiesSnap.size, activeCompanies: activeCount }));
    }, error => {
      console.error('Erro ao escutar empresas:', error);
    });


    // Use collectionGroup listener to aggregate reservations across companies
    const reservationsGroupUnsub = onSnapshot(reservationsGroup, reservationsSnap => {
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const totals = new Map();

      reservationsSnap.forEach(rdoc => {
        const reservation = rdoc.data();
        // parent of reservations collection is the company document
        const companyRef = rdoc.ref.parent.parent;
        if (!companyRef) return;
        const companyId = companyRef.id;

        // Only count completed or confirmed reservations with payments
        if ((reservation.status === 'confirmed' || reservation.status === 'completed') && reservation.paidAmount > 0) {
          const amount = parseFloat(reservation.paidAmount) || 0;
          const entry = totals.get(companyId) || { total: 0, month: 0 };
          entry.total += amount;

          const created = reservation.createdAt && reservation.createdAt.toDate ? reservation.createdAt.toDate() : (reservation.createdAt ? new Date(reservation.createdAt) : null);
          if (created && created >= firstDayOfMonth) entry.month += amount;

          totals.set(companyId, entry);
        }
      });

      // Merge totals into companies state
      setCompanies(prevCompanies => {
        const merged = prevCompanies.map(c => {
          const sums = totals.get(c.id) || { total: 0, month: 0 };
          return { ...c, revenue: sums.total, monthRevenue: sums.month };
        }).sort((a, b) => (b.revenue || 0) - (a.revenue || 0));

        // Update stats totals
        const totalRevenue = Array.from(totals.values()).reduce((s, t) => s + t.total, 0);
        const monthRevenue = Array.from(totals.values()).reduce((s, t) => s + t.month, 0);
        const systemNetReceipt = merged.reduce((sum, company) => sum + calculateSystemNetReceipt({
          monthRevenue: company.monthRevenue,
          monthlyPrice: company.billingSettings?.customMonthlyPrice ?? company.billingSettings?.monthlyPrice,
          systemRevenuePercentage: company.billingSettings?.systemRevenuePercentage,
          monthlyCostDeductionPercentage: company.billingSettings?.monthlyCostDeductionPercentage
        }).netReceipt, 0);
        setStats(prev => ({ ...prev, totalRevenue, monthRevenue, systemNetReceipt, loading: false }));

        return merged;
      });
    }, error => {
      console.error('Erro ao escutar reservas:', error);
      setStats(prev => ({ ...prev, loading: false }));
    });

    // Access logs listener (últimos 10)
    const logsRef = collection(db, 'accessLogs');
    const logsQuery = query(logsRef, orderBy('timestamp', 'desc'), limit(10));
    const logsUnsub = onSnapshot(logsQuery, logsSnap => {
      const logsData = logsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAccessLogs(logsData);
    }, error => {
      console.error('Erro ao escutar logs:', error);
    });

    const announcementsQuery = query(collection(db, 'systemAnnouncements'), orderBy('createdAt', 'desc'), limit(20));
    const announcementsUnsub = onSnapshot(announcementsQuery, snapshot => {
      setAnnouncements(snapshot.docs.map(item => ({ id: item.id, ...item.data() })));
    }, error => console.error('Erro ao carregar comunicados:', error));

    const receiptsQuery = query(collectionGroup(db, 'billingReceipts'), limit(50));
    const receiptsUnsub = onSnapshot(receiptsQuery, snapshot => {
      const nextReceipts = snapshot.docs
        .map(item => ({ id: item.id, ...item.data() }))
        .sort((a, b) => (b.receiptSubmittedAt?.toDate?.() || new Date(0)) - (a.receiptSubmittedAt?.toDate?.() || new Date(0)));
      if (receiptsInitialized.current && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        nextReceipts
          .filter(item => !seenReceiptIds.current.has(`${item.companyId}-${item.id}`))
          .forEach(item => {
            if ('serviceWorker' in navigator) {
              navigator.serviceWorker.ready.then(registration => registration.showNotification('Novo comprovante recebido', {
                body: `${item.companyName || 'Empresa'} enviou ${item.receiptName || 'um comprovante'}.`,
                icon: '/logo192.png',
                tag: `billing-receipt-${item.companyId}-${item.id}`,
                data: { url: '/' }
              })).catch(() => {});
            }
          });
      }
      nextReceipts.forEach(item => seenReceiptIds.current.add(`${item.companyId}-${item.id}`));
      receiptsInitialized.current = true;
      setBillingReceipts(nextReceipts);
    }, error => console.error('Erro ao carregar comprovantes:', error));

    const errorsQuery = query(collection(db, 'rootNotifications'), limit(50));
    const errorsUnsub = onSnapshot(errorsQuery, snapshot => {
      const nextErrors = snapshot.docs
        .map(item => ({ id: item.id, ...item.data() }))
        .filter(item => item.type === 'system_error')
        .sort((a, b) => (b.createdAt?.toDate?.() || new Date(0)) - (a.createdAt?.toDate?.() || new Date(0)));
      if (errorsInitialized.current && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        nextErrors
          .filter(item => !seenErrorIds.current.has(item.id))
          .forEach(item => {
            if ('serviceWorker' in navigator) {
              navigator.serviceWorker.ready.then(registration => registration.showNotification(item.title || 'Erro no sistema', {
                body: item.message,
                icon: '/logo192.png',
                tag: `system-error-${item.id}`,
                data: { url: '/' }
              })).catch(() => {});
            }
          });
      }
      nextErrors.forEach(item => seenErrorIds.current.add(item.id));
      errorsInitialized.current = true;
      setSystemErrors(nextErrors);
    }, error => console.error('Erro ao carregar relatos do sistema:', error));

    return () => {
      companiesUnsub && companiesUnsub();
      reservationsGroupUnsub && reservationsGroupUnsub();
      logsUnsub && logsUnsub();
      announcementsUnsub && announcementsUnsub();
      receiptsUnsub && receiptsUnsub();
      errorsUnsub && errorsUnsub();
    };
  }, []);

  

  const clearAccessLogs = async () => {
    if (!window.confirm('Deseja realmente limpar todos os logs de acesso?')) {
      return;
    }

    try {
      const logsRef = collection(db, 'accessLogs');
      const logsSnap = await getDocs(logsRef);

      const deletePromises = logsSnap.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      setAccessLogs([]);
      alert('Logs de acesso limpos com sucesso!');
    } catch (error) {
      console.error('Erro ao limpar logs:', error);
      alert('Erro ao limpar logs: ' + error.message);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getDueDate = company => {
    const now = new Date();
    const dueDay = Math.min(Number(company.billingSettings?.dueDay) || 10, new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate());
    return new Date(now.getFullYear(), now.getMonth(), dueDay, 12);
  };

  const openNoticeModal = company => {
    const dueDate = company ? getDueDate(company) : null;
    const amount = company?.billingSettings?.customMonthlyPrice ?? company?.billingSettings?.monthlyPrice ?? '';
    setNoticeForm({
      companyId: company?.id || '',
      type: 'billing_due',
      title: 'Cobrança do plano',
      message: company ? `A mensalidade de ${company.name} está disponível para pagamento.` : '',
      dueDate: dueDate ? dueDate.toISOString().slice(0, 10) : '',
      amount: amount === '' ? '' : Number(amount),
      pixKey: company?.billingSettings?.pixKey || ''
    });
    setShowNoticeModal(true);
  };

  const sendAdminNotice = async event => {
    event.preventDefault();
    const company = companies.find(item => item.id === noticeForm.companyId);
    if (!company || !noticeForm.title.trim() || !noticeForm.message.trim()) return;
    setSendingNotice(true);
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const admins = usersSnapshot.docs
        .map(adminDoc => ({ id: adminDoc.id, ...adminDoc.data() }))
        .filter(admin => {
          if (admin.role !== 'admin') return false;
          if (admin.companyId === company.id || admin.currentCompanyId === company.id) return true;
          return Array.isArray(admin.companies) && admin.companies.some(companyLink => (
            companyLink === company.id ||
            companyLink?.companyId === company.id ||
            companyLink?.id === company.id
          ));
        });
      const companyEmail = String(company.email || '').trim();
      if (!companyEmail) throw new Error('A empresa não possui email cadastrado para receber o aviso.');

      const billingDetails = noticeForm.type === 'billing_due'
        ? `${noticeForm.amount !== '' ? ` Valor: ${formatCurrency(noticeForm.amount)}.` : ''}${noticeForm.dueDate ? ` Vencimento: ${new Date(`${noticeForm.dueDate}T12:00:00`).toLocaleDateString('pt-BR')}.` : ''}`
        : '';
      const effectiveMessage = `${noticeForm.message.trim()}${billingDetails}`;

      const announcementRef = doc(collection(db, 'systemAnnouncements'));
      const batch = writeBatch(db);
      batch.set(announcementRef, {
        ...noticeForm,
        message: effectiveMessage,
        amount: noticeForm.amount === '' ? null : Number(noticeForm.amount),
        companyName: company.name || '',
        recipientCount: admins.length,
        emailRecipient: companyEmail,
        createdAt: serverTimestamp()
      });
      admins.forEach(admin => {
        batch.set(doc(collection(db, `companies/${company.id}/userNotifications`)), {
          companyId: company.id,
          recipientUserId: admin.uid || admin.id,
          recipientEmail: admin.email || '',
          title: noticeForm.title.trim(),
          message: effectiveMessage,
          type: noticeForm.type,
          actionPath: '/',
          read: false,
          createdAt: serverTimestamp(),
          metadata: {
            announcementId: announcementRef.id,
            companyName: company.name || '',
            dueDate: noticeForm.dueDate || null,
            amount: noticeForm.amount === '' ? null : Number(noticeForm.amount),
            pixKey: noticeForm.pixKey.trim() || null
          }
        });
      });
      await batch.commit();
      const emailResult = await sendCompanyNoticeEmail({
        companyId: company.id,
        to: companyEmail,
        companyName: company.name || '',
        title: noticeForm.title.trim(),
        message: effectiveMessage,
        type: noticeForm.type,
        dueDate: noticeForm.dueDate || '',
        amount: noticeForm.amount === '' ? null : Number(noticeForm.amount),
        pixKey: noticeForm.pixKey.trim() || ''
      });
      if (!emailResult.success) throw new Error(`Aviso registrado, mas o email não foi enviado: ${emailResult.error}`);
      setShowNoticeModal(false);
      alert(`Aviso enviado para ${companyEmail}${admins.length ? ` e ${admins.length} administrador(es)` : ''}.`);
    } catch (error) {
      console.error('Erro ao enviar aviso:', error);
      alert('Erro ao enviar aviso: ' + error.message);
    } finally {
      setSendingNotice(false);
    }
  };

  const markBillingPaid = async company => {
    const dueDate = getDueDate(company);
    const monthKey = `${dueDate.getFullYear()}-${String(dueDate.getMonth() + 1).padStart(2, '0')}`;
    if (!window.confirm(`Confirmar o pagamento de ${company.name} referente a ${monthKey}?`)) return;
    try {
      await updateDoc(doc(db, 'companies', company.id), {
        'billingSettings.lastPaidMonthKey': monthKey,
        'billingSettings.lastPaidAt': serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Erro ao confirmar cobrança:', error);
      alert('Erro ao confirmar cobrança: ' + error.message);
    }
  };

  const grantBillingFreeMonth = async company => {
    const dueDate = getDueDate(company);
    const monthKey = `${dueDate.getFullYear()}-${String(dueDate.getMonth() + 1).padStart(2, '0')}`;
    if (!window.confirm(`Registrar gratuidade da mensalidade de ${company.name} referente a ${monthKey}?`)) return;
    try {
      await updateDoc(doc(db, 'companies', company.id), {
        'billingSettings.freeMonthKey': monthKey,
        'billingSettings.freeMonthAt': serverTimestamp(),
        'billingSettings.freeMonthReason': 'Gratuidade concedida pelo Root',
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Erro ao registrar gratuidade:', error);
      alert('Erro ao registrar gratuidade: ' + error.message);
    }
  };

  const companyUsage = companies
    .map(company => ({
      ...company,
      accessCount: accessLogs.filter(log => log.companyId === company.id || log.details?.companyId === company.id).length
    }))
    .sort((a, b) => b.accessCount - a.accessCount);
  const pageUsage = accessLogs.reduce((result, log) => {
    const page = log.details?.path || log.details?.page || log.action || 'Ação não identificada';
    result[page] = (result[page] || 0) + 1;
    return result;
  }, {});
  const pageUsageList = Object.entries(pageUsage).sort(([, a], [, b]) => b - a).slice(0, 8);
  const maxPageUsage = Math.max(...pageUsageList.map(([, count]) => count), 1);
  const unreadSystemErrors = systemErrors.filter(item => !item.read).length;

  if (stats.loading) {
    return (
      <div className="root-dashboard">
        <LoadingState title="Carregando painel root" subtitle="Consolidando empresas, receita e acessos" />
      </div>
    );
  }

  return (
    <div className="root-dashboard">
      <div className="dashboard-header">
        <h1>🔥 Painel Root - SDC Turismo</h1>
        <p>Visão geral de todas as empresas do sistema</p>
      </div>

      {/* Stats Cards */}
      {activeTab === 'panel' && <>
      <div className="stats-grid">
        <div className="stat-card" style={{ borderTopColor: '#667eea' }}>
          <div className="stat-icon" style={{ backgroundColor: '#667eea' }}>
            <Building2 size={24} color="white" />
          </div>
          <div className="stat-content">
            <p className="stat-label">Total de Empresas</p>
            <h2 className="stat-value">{stats.totalCompanies}</h2>
            <span className="stat-subtitle">{stats.activeCompanies} ativas</span>
          </div>
        </div>

        <div className="stat-card" style={{ borderTopColor: '#4caf50' }}>
          <div className="stat-icon" style={{ backgroundColor: '#4caf50' }}>
            <DollarSign size={24} color="white" />
          </div>
          <div className="stat-content">
            <p className="stat-label">Faturamento Total</p>
            <h2 className="stat-value">{formatCurrency(stats.totalRevenue)}</h2>
            <span className="stat-subtitle">Todas as empresas</span>
          </div>
        </div>

        <div className="stat-card" style={{ borderTopColor: '#ff9800' }}>
          <div className="stat-icon" style={{ backgroundColor: '#ff9800' }}>
            <TrendingUp size={24} color="white" />
          </div>
          <div className="stat-content">
            <p className="stat-label">Faturamento do Mês</p>
            <h2 className="stat-value">{formatCurrency(stats.monthRevenue)}</h2>
            <span className="stat-subtitle">{new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</span>
          </div>
        </div>

        <div className="stat-card" style={{ borderTopColor: '#0f766e' }}>
          <div className="stat-icon" style={{ backgroundColor: '#0f766e' }}>
            <DollarSign size={24} color="white" />
          </div>
          <div className="stat-content">
            <p className="stat-label">Recebimento líquido do sistema</p>
            <h2 className="stat-value">{formatCurrency(stats.systemNetReceipt)}</h2>
            <span className="stat-subtitle">Faturamento mensal menos custo proporcional</span>
          </div>
        </div>
      </div>

      <div className="root-section analytics-section">
        <div className="section-header"><div><h2><BarChart3 size={20} /> Análises de uso</h2><p>Uso das empresas, páginas acessadas e alertas do sistema.</p></div></div>
        <div className="analytics-grid">
          <section className="analytics-card"><h3>Uso por empresa</h3><div className="usage-bars">{companyUsage.slice(0, 6).map(company => <div className="usage-bar-row" key={company.id}><span title={company.name}>{company.name}</span><div><i style={{ width: `${companyUsage[0]?.accessCount ? Math.max((company.accessCount / companyUsage[0].accessCount) * 100, company.accessCount ? 5 : 0) : 0}%` }} /></div><b>{company.accessCount}</b></div>)}</div>{companyUsage.length === 0 && <p className="analytics-empty">Nenhuma empresa cadastrada.</p>}</section>
          <section className="analytics-card"><h3>Páginas e ações mais acessadas</h3><div className="usage-bars">{pageUsageList.map(([page, count]) => <div className="usage-bar-row" key={page}><span title={page}>{page}</span><div><i style={{ width: `${(count / maxPageUsage) * 100}%` }} /></div><b>{count}</b></div>)}</div>{pageUsageList.length === 0 && <p className="analytics-empty">Nenhum acesso registrado.</p>}</section>
        </div>
        <section className="analytics-card error-summary-card"><div><h3><AlertTriangle size={18} /> Notificações de erros</h3><p>{unreadSystemErrors} não lida(s) de {systemErrors.length} relato(s).</p></div><strong>{unreadSystemErrors}</strong></section>
        {systemErrors.length > 0 && <section className="analytics-card system-error-list"><div className="analytics-card__title"><h3>Relatos recentes</h3><span>{systemErrors.length}</span></div>{systemErrors.slice(0, 5).map(item => <div className="system-error-item" key={item.id}><div><strong>{item.title || 'Erro no sistema'}</strong><small>{item.companyName || item.companyId || 'Empresa'} · {item.senderName || 'Administrador'} · {formatDate(item.createdAt)}</small><p>{item.message}</p></div><button type="button" className="table-action" onClick={() => setSelectedSystemError(item)}>Ver alerta</button></div>)}</section>}
      </div>
      </>}

      {activeTab === 'billing' && <div className="root-section billing-section">
        <div className="section-header">
          <div>
            <h2><BellRing size={20} /> Cobranças e comunicados</h2>
            <p>Acompanhe vencimentos e envie alertas aos administradores.</p>
          </div>
          <button className="btn-primary root-notice-button" onClick={() => openNoticeModal()}><Plus size={18} /> Novo aviso</button>
        </div>
        <div className="companies-table">
          <table>
            <thead><tr><th>Empresa</th><th>Valor</th><th>Vencimento</th><th>Situação</th><th>Ação</th></tr></thead>
            <tbody>{companies.map(company => {
              const dueDate = getDueDate(company);
              const monthKey = `${dueDate.getFullYear()}-${String(dueDate.getMonth() + 1).padStart(2, '0')}`;
              const paid = company.billingSettings?.lastPaidMonthKey === monthKey;
              const free = company.billingSettings?.freeMonthKey === monthKey;
              const overdue = !paid && !free && dueDate < new Date();
              const amount = company.billingSettings?.customMonthlyPrice ?? company.billingSettings?.monthlyPrice ?? 0;
              return <tr key={`billing-${company.id}`}>
                <td data-label="Empresa"><strong>{company.name}</strong></td>
                <td data-label="Valor">{formatCurrency(amount)}</td>
                <td data-label="Vencimento">{dueDate.toLocaleDateString('pt-BR')}</td>
                <td data-label="Situação"><span className={`billing-status ${free ? 'free' : paid ? 'paid' : overdue ? 'overdue' : 'scheduled'}`}>{free ? 'Gratuita' : paid ? 'Paga' : overdue ? 'Vencida' : 'Programada'}</span></td>
                <td data-label="Ação"><div className="billing-actions"><button className="table-action" onClick={() => openNoticeModal(company)}><Send size={16} /> Alertar</button>{!paid && !free && <><button className="table-action payment-action" onClick={() => markBillingPaid(company)}>Dar baixa</button><button className="table-action free-action" onClick={() => grantBillingFreeMonth(company)}>Gratuidade</button></>}</div></td>
              </tr>;
            })}</tbody>
          </table>
        </div>
        {announcements.length > 0 && <div className="notice-history">
          <h3>Últimos avisos enviados</h3>
          {announcements.slice(0, 5).map(item => <div key={item.id}><span><strong>{item.title}</strong><small>{item.companyName} · {formatDate(item.createdAt)}</small></span>{item.receiptUrl ? <a href={item.receiptUrl} target="_blank" rel="noreferrer">Ver comprovante</a> : <b>{item.recipientCount || 0} destinatário(s)</b>}</div>)}
        </div>}
        {billingReceipts.length > 0 && <div className="billing-receipt-alerts">
          <div className="billing-receipt-alerts__header">
            <div><strong>Comprovantes recebidos</strong><span>Confira os pagamentos enviados pelas empresas.</span></div>
            <span className="billing-receipt-alerts__count">{billingReceipts.length}</span>
          </div>
          {billingReceipts.slice(0, 5).map(receipt => <div className="billing-receipt-alert" key={`${receipt.companyId}-${receipt.id}`}>
            <div><strong>{receipt.companyName || receipt.companyId || 'Empresa'}</strong><small>{receipt.receiptName || 'Comprovante'} · {formatDate(receipt.receiptSubmittedAt)}</small></div>
            <button type="button" className="table-action" onClick={() => setSelectedReceipt(receipt)}>Ver imagem</button>
          </div>)}
        </div>}
      </div>}

      {activeTab === 'companies' && <Companies />}

      {/* Access Logs */}
      {activeTab === 'logs' && <div className="root-section">
        <div className="section-header">
          <h2>🔐 Logs de Acesso</h2>
          <button 
            className="btn-danger"
            onClick={clearAccessLogs}
            disabled={accessLogs.length === 0}
          >
            Limpar Logs
          </button>
        </div>
        <div className="logs-table">
          <table>
            <thead>
              <tr>
                <th>Data/Hora</th>
                <th>Usuário</th>
                <th>Ação</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {accessLogs.length === 0 ? (
                <tr>
                  <td colSpan="4" className="empty-state">
                    <AlertCircle size={32} />
                    <p>Nenhum log de acesso registrado</p>
                  </td>
                </tr>
              ) : (
                accessLogs.map(log => (
                  <tr key={log.id}>
                    <td data-label="Data/Hora">{formatDate(log.timestamp)}</td>
                    <td data-label="Usuário">
                      {log.userName || log.userEmail}
                      <br />
                      <small style={{ color: '#666' }}>{log.userRole}</small>
                    </td>
                    <td data-label="Ação">{log.action}</td>
                    <td data-label="IP"><code>{log.ip || '-'}</code></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>}

      {showNoticeModal && <div className="root-modal-overlay" onClick={() => setShowNoticeModal(false)}>
        <form className="root-notice-modal" onSubmit={sendAdminNotice} onClick={event => event.stopPropagation()}>
          <header><div><h2>Enviar aviso</h2><p>O alerta aparecerá para todos os administradores da empresa.</p></div><button type="button" onClick={() => setShowNoticeModal(false)} aria-label="Fechar"><X size={20} /></button></header>
          <label>Empresa<select required value={noticeForm.companyId} onChange={event => setNoticeForm(current => ({ ...current, companyId: event.target.value }))}><option value="">Selecione</option>{companies.map(company => <option key={company.id} value={company.id}>{company.name}</option>)}</select></label>
          <label>Tipo<select value={noticeForm.type} onChange={event => setNoticeForm(current => ({ ...current, type: event.target.value }))}><option value="billing_due">Cobrança</option><option value="new_feature">Nova funcionalidade</option><option value="usage_exemption">Isenção de uso</option><option value="general">Comunicado geral</option></select></label>
          <label>Título<input required value={noticeForm.title} onChange={event => setNoticeForm(current => ({ ...current, title: event.target.value }))} /></label>
          <label>Mensagem<textarea required rows="4" value={noticeForm.message} onChange={event => setNoticeForm(current => ({ ...current, message: event.target.value }))} /></label>
          {noticeForm.type === 'billing_due' && <><div className="notice-fields"><label>Vencimento<input type="date" value={noticeForm.dueDate} onChange={event => setNoticeForm(current => ({ ...current, dueDate: event.target.value }))} /></label><label>Valor<input type="number" min="0" step="0.01" value={noticeForm.amount} onChange={event => setNoticeForm(current => ({ ...current, amount: event.target.value }))} /></label></div><label>Chave PIX<input value={noticeForm.pixKey} onChange={event => setNoticeForm(current => ({ ...current, pixKey: event.target.value }))} placeholder="CPF, CNPJ, e-mail, telefone ou chave aleatória" /></label></>}
          <footer><button type="button" className="btn-secondary" onClick={() => setShowNoticeModal(false)}>Cancelar</button><button className="btn-primary" disabled={sendingNotice}><Send size={17} /> {sendingNotice ? 'Enviando...' : 'Enviar aviso'}</button></footer>
        </form>
      </div>}

      {selectedReceipt && <div className="root-modal-overlay" onClick={() => setSelectedReceipt(null)}>
        <section className="root-receipt-modal" onClick={event => event.stopPropagation()}>
          <header><div><span>Comprovante recebido</span><h2>{selectedReceipt.companyName || 'Pagamento da empresa'}</h2><p>{selectedReceipt.receiptName || 'Arquivo enviado pelo administrador'}</p></div><button type="button" onClick={() => setSelectedReceipt(null)} aria-label="Fechar"><X size={20} /></button></header>
          <div className="root-receipt-preview">{selectedReceipt.receiptType === 'application/pdf' ? <iframe src={selectedReceipt.receiptUrl} title={`Comprovante de pagamento de ${selectedReceipt.companyName || 'empresa'}`} /> : <img src={selectedReceipt.receiptUrl} alt={`Comprovante de pagamento de ${selectedReceipt.companyName || 'empresa'}`} />}</div>
          <footer><small>Enviado por {selectedReceipt.receiptSubmittedByName || selectedReceipt.receiptSubmittedBy || 'administrador'}</small><a href={selectedReceipt.receiptUrl} target="_blank" rel="noreferrer">Abrir arquivo</a></footer>
        </section>
      </div>}

      {selectedSystemError && <div className="root-modal-overlay" onClick={() => setSelectedSystemError(null)}>
        <section className="root-receipt-modal system-error-modal" onClick={event => event.stopPropagation()}>
          <header><div><span>Alerta de erro</span><h2>{selectedSystemError.title || 'Erro no sistema'}</h2><p>{selectedSystemError.companyName || selectedSystemError.companyId} · enviado por {selectedSystemError.senderName || 'Administrador'}</p></div><button type="button" onClick={() => setSelectedSystemError(null)} aria-label="Fechar"><X size={20} /></button></header>
          <div className="system-error-modal__message">{selectedSystemError.message}</div>
          <footer><small>{formatDate(selectedSystemError.createdAt)}</small><button type="button" onClick={() => setSelectedSystemError(null)}>Fechar</button></footer>
        </section>
      </div>}
    </div>
  );
};

export default RootDashboard;
